import * as React from "react"
import {
  Button,
  Text,
  StyledPanelBoilerplate,
  StyledPanelBodySection,
  InputConnectedField,
  StyledModalActions,
  ThemeCss,
  Spacer,
} from "gatsby-interface"
import Form, { FormElement } from "@modules/form/components/Form"
import * as Yup from "yup"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { ui as uiText, siteTransfer as text } from "@modules/locales/default.js"
import {
  CloudResourceType,
  CloudRole,
  InvitationEmailResourceCreateInput,
  InvitationType,
} from "@modules/graphql/types"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { useCurrentUser } from "@modules/auth"
import { FormikHelpers } from "formik"

type FormValues = {
  email: string
  name: string
}

const validationSchema = Yup.object().shape<FormValues>({
  email: Yup.string()
    .email("Client email must be a valid email address")
    .required("Client email is required"),
  name: Yup.string().required("Client name is required"),
})

type TransferSiteModalProps = {
  isOpen: boolean
  siteId: string
  siteName: string
  onClose: () => void
  onTransfer: (
    invitations: InvitationEmailResourceCreateInput[]
  ) => Promise<void> | undefined
  transfering: boolean
  error: JSX.Element | null
}

export function TransferSiteModal({
  isOpen = false,
  siteId,
  siteName,
  onClose,
  onTransfer,
  transfering,
  error,
}: TransferSiteModalProps) {
  const [setError, errorAlert] = useTriggerErrorAlert()
  const { currentUser } = useCurrentUser()

  const handleSubmit = (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    const { setErrors } = actions

    if (values.email === currentUser?.email) {
      return setErrors({ email: `You cannot transfer the site to yourself.` })
    }

    const invitations = [
      {
        type: InvitationType.ParentOrganization,
        email: values.email,
        name: values.name,
        resources: [
          {
            resourceType: CloudResourceType.Site,
            role: CloudRole.Owner,
            resourceId: siteId,
          },
        ],
      },
    ]

    return onTransfer && onTransfer(invitations)
  }

  const handleCancel = () => {
    onClose()
    setError(null)
  }

  return (
    <StyledPanelBoilerplate
      aria-label={text.headers.transferSite}
      header={text.headers.transferSite}
      isOpen={isOpen}
      onClose={onClose}
    >
      <StyledPanelBodySection css={{ paddingBottom: 0 }}>
        <Text>
          <FormattedMessage<"site", "strong">
            message={text.messages.transferSiteToNewAccount}
            values={{
              site: siteName,
            }}
            tags={commonMessageTags}
          />
        </Text>
        <ul css={bulletsCss}>
          <li>
            <FormattedMessage<"days", "strong">
              message={text.messages.clientWillReceiveEmail}
              values={{
                days: `7 days`,
              }}
              tags={commonMessageTags}
            />
          </li>
          <li>
            <FormattedMessage<"role", "strong">
              message={text.messages.youWillRetainAccessToSite}
              values={{
                role: `Admin user`,
              }}
              tags={commonMessageTags}
            />
          </li>
        </ul>

        <Spacer direction="horizontal" size={5} />

        <div>
          <Form<FormValues>
            validationSchema={validationSchema}
            initialValues={{
              email: ``,
              name: ``,
            }}
            onSubmit={handleSubmit}
          >
            {() => (
              <FormElement applySpacing noValidate css={formCss}>
                <InputConnectedField
                  label={text.labels.clientEmail}
                  name="email"
                  required
                  type="text"
                  placeholder="john.doe@example.com"
                />
                <InputConnectedField
                  label={text.labels.clientName}
                  name="name"
                  required
                  type="text"
                  placeholder="e. g. Foobank"
                  hint={text.messages.weUseThisInformatioToPersonalize}
                />
                {errorAlert} {error}
                <StyledModalActions>
                  <Button
                    variant="SECONDARY"
                    type="reset"
                    onClick={handleCancel}
                  >
                    {uiText.actions.cancel}
                  </Button>
                  <Button
                    type="submit"
                    loading={transfering}
                    loadingLabel="Transfering"
                  >
                    {text.labels.transferSite}
                  </Button>
                </StyledModalActions>
              </FormElement>
            )}
          </Form>
        </div>
      </StyledPanelBodySection>
    </StyledPanelBoilerplate>
  )
}

/* styles */

const bulletsCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],

  li: {
    marginBottom: 0,
  },
})

const formCss: ThemeCss = theme => ({
  display: `grid`,
  gap: theme.space[8],
})
